/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { KeyboardEvent, useEffect, useState } from "react";
import styled from "styled-components";
import { InputSearch, theme } from "@nordcloud/gnui";
import {
  maxSearchLength,
  minSearchLength,
} from "~/components/SearchInput/const";
import { QuerySearch } from "~/components/SearchInput/types";
import { QueryState, useQueryState, useSleep } from "~/hooks";
import { isNotNil } from "~/tools";

type Props = {
  disabled?: boolean;
  delayedSearch?: boolean;
  placeholder?: string;
  minSearchLength?: number;
  searchDelay?: number;
  onSubmit: (query: string) => void;
};

export function TableSearchInput<T extends QuerySearch>({
  onSubmit,
  placeholder = "Search",
  searchDelay = 600,
  disabled = false,
}: Props) {
  const {
    queryState: { searchPhrase },
    updateQueryState,
  } = useQueryState<T>();

  const [searchInput, setSearchInput] = useState<string>(searchPhrase ?? "");
  const { sleep, cancelSleep } = useSleep();

  useEffect(() => {
    setSearchInput(searchPhrase ?? "");
  }, [searchPhrase, setSearchInput]);

  const onSearch = (phrase?: string) => {
    const toSearch = isNotNil(phrase) ? phrase : searchInput;
    if (toSearch && shouldSearch(toSearch)) {
      onSubmit(toSearch);
    }
  };

  const handleKeyPress = (
    event: KeyboardEvent<HTMLInputElement> & React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      updateQueryState({ searchPhrase: event.target.value } as T);
      onSearch(event.target.value);
    }
  };

  const shouldSearch = (query: string): boolean => {
    return query.length >= minSearchLength;
  };

  const handleDelayedSearch = async (phrase: string) => {
    cancelSleep();
    await sleep(searchDelay);
    onSearch(phrase);
  };

  return (
    <Div>
      <InputSearch
        onKeyDown={handleKeyPress}
        value={searchInput ?? ""}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const val = e.target.value;
          setSearchInput(val);
          handleDelayedSearch(val).then(() =>
            updateQueryState({ page: "1", searchPhrase: val } as QueryState & T)
          );
        }}
        placeholder={placeholder}
        onSearch={onSearch}
        name="search"
        minLength={minSearchLength}
        maxLength={maxSearchLength}
        disabled={disabled}
      />
    </Div>
  );
}

const Div = styled.div`
  width: 100%;
  input:invalid {
    color: ${theme.color.background.error};
  }
`;
